import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../redux/actions/billing';
import PublicistIcon from '../../assets/images/icons/publicist.svg';
import MarketingManagerIcon from '../../assets/images/icons/marketing-manager.svg';
import ArtistIcon from '../../assets/images/icons/artist.svg';
import AudioIcon from '../../assets/images/icons/audio.svg';
import DistributionIcon from '../../assets/images/icons/distribution.svg';
import CreatorIcon from '../../assets/images/icons/creator.svg';
import AnalystIcon from '../../assets/images/icons/analyst.svg';
import ProfessorUIcon from '../../assets/images/icons/professor.svg';
import AmplitudeIcon from '../../assets/images/icons/button-icon.svg';
import StarIcon from '../../assets/images/icons/start-icon.svg';
import CrossIcon from '../../assets/images/icons/cross.svg';
import { PricingPlanModal } from '../PricingPlanModal';
import { CreditPlanModal } from '../CreditPlanModal';
import ToolsIcon from '../../assets/images/icons/tools-icon.png'
import { DistributionModal } from '../DistributionAccess/DistributionModal';
import { hasArtistLinked } from '../../helper/access';

export const AdminSidebar = ({ isCollapsed, toggleSidebar }) => {
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [showDistributionModal, setShowDistributionModal] = useState(false);
    const [showLinkArtistRequiredModal, setShowLinkArtistRequiredModal] = useState(false);
    const [loading, setLoading] = useState(false);
    // Close Sidebar Drawer
    const closeSidebar = () => {
        document.body.classList.remove('admin-sidebar-open');
    };

    const closeSidebarOnMobile = () => {
        if (window.innerWidth < 1024) closeSidebar();
    };
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const access = useSelector((state) => state.auth.access);
    const userDetails = useSelector((state) => state.auth.userDetails);

    const {
        hasActiveSubscription,
        subscriptionDetails,
        creditDetails,
        subscriptionPlan,
        lastSubscriptionFetched,
    } = useSelector((state) => state.billing);

    const planList = subscriptionPlan || [];
    const billingData = subscriptionDetails?.data;
    const lastBillingFetched = subscriptionDetails?.lastFetched;

    const credits = creditDetails?.data || { total: 0, remaining: 0 };
    const isBillingResolved = Boolean(lastBillingFetched && billingData && creditDetails?.lastFetched);

    const artistLinked = hasArtistLinked(access, userDetails);
    const showLinkArtistButton = isBillingResolved && !artistLinked;

    // Fetch plan list only if cache expired or missing
    const hasFetched = useRef(false);
    useEffect(() => {
        const fetchPlans = async () => {
            const shouldFetch =
                !planList?.length ||
                !lastSubscriptionFetched ||
                Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

            if (shouldFetch && !hasFetched.current) {
                hasFetched.current = true; 
                await dispatch(actions.getPlanList("subscription"));
                // await dispatch(actions.getUserPurchases());
            }
        };

        fetchPlans();

        return () => {
            hasFetched.current = false;
        };
    }, [dispatch, planList?.length, lastSubscriptionFetched]);

    useEffect(() => {
        const fetchBillingStatus = async () => {
            const shouldFetchStatus =
                !billingData ||
                !lastBillingFetched ||
                Date.now() - lastBillingFetched > 1000 * 60 * 10; // 10 min cache

            if (shouldFetchStatus) {
                await dispatch(actions.getBillingDetails());
                await dispatch(actions.getUserPurchases({}, 'all'));
            }
        };
        fetchBillingStatus();
    }, [dispatch, billingData, lastBillingFetched]);

    // Credit progress bar
    const progress = credits.total > 0 ? (credits.remaining / credits.total) * 100 : 0;

    const handleLinkArtistClick = () => {
        setShowLinkArtistRequiredModal(false);
        navigate("/link-accounts");
        closeSidebar();
    };

    const handleGoProClick = async () => {
        if (!isBillingResolved || loading) return;

        if (hasActiveSubscription) {
            try {
                setLoading(true);
                const opened = await dispatch(actions.getCustomerPortalSession());
                if (opened) return;
            } catch (err) {
                console.error("Error opening customer portal:", err);
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!artistLinked) {
            setShowLinkArtistRequiredModal(true);
            return;
        }

        setShowPricingPlanModal(true);
    };

    const handleOpenCreditModal = () => {
        setShowCreditPlanModal(true);
    };

    const handleShowAlert = (page) => {
        alert(page + " is a Work in Progress")
    }

    const handleShowDistribution = () => {
        setShowDistributionModal(!showDistributionModal);
    }
    return (
        <>
            <aside className={`admin_sidebar fixed top-0 left-0 w-[200px] ${isCollapsed ? 'lg:w-[70px]' : 'lg:w-[200px]'} -translate-x-full lg:translate-x-0 z-[60]`} data-tour="sidebar">
                {/* Main Navbar Section */}
                <nav className="bg-gradient-to-b from-theme_clr1 to-theme_clr2 h-full overflow-hidden px-2 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] 2xl:py-5">
                    <div className='flex flex-col gap-4 md:gap-6 h-full min-h-0'>
                        <div className={`top-section logo-wrapper shrink-0 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} max-md:border-b max-md:border-slate-600 max-md:pb-3`} data-tour="sidebar-logo">
                            <Link to={'/'}>
                                {isCollapsed ?
                                    <img src={require('../../assets/images/short-logo.png')} alt="Logo" className="w-[30px] h-[40px] mx-auto" title='Expand Sidebar' /> :
                                    <img src={require('../../assets/images/logo.png')} alt="Logo" className="max-w-[100px] md:max-w-[110px] mx-auto" />
                                }
                            </Link>
                            <button onClick={closeSidebar} className="text-white lg:hidden" aria-label="Close menu">
                                <img src={CrossIcon} alt="Cancel" className="block" />
                            </button>
                            {isCollapsed ?
                                <button onClick={toggleSidebar} className="text-white hidden lg:block" title='Expand Sidebar'>
                                    <i className="bi bi-caret-right-square"></i>
                                </button> :
                                <button onClick={toggleSidebar} className="text-white hidden lg:block" title='Collapse Sidebar'>
                                    <i className="bi bi-caret-left-square"></i>
                                </button>
                            }
                        </div>
                        <div className="middle-section menu-wrapper flex-1 min-h-0 overflow-y-auto overscroll-contain pe-1" data-tour="sidebar-menu">
                            <div className="label text-white text-base font-medium mb-1">
                                Roles
                            </div>

                            <ul
                                className="list-none flex flex-col gap-2 2xl:gap-4"
                                onClick={(event) => {
                                    if (event.target.closest("a")) closeSidebarOnMobile();
                                }}
                            >

                                {/* Amplitude AI */}
                                <li>
                                    <NavLink
                                        to="/admin/amplitude-ai"
                                        className={({ isActive }) =>
                                            `admin-sidebar-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <img
                                            src={AmplitudeIcon}
                                            alt="AmplitudeAI"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title">Amplitude AI</span>
                                        )}
                                    </NavLink>
                                </li>

                                {/* UrStudio */}
                                <li>
                                    <NavLink
                                        to='/admin/urstudio'
                                        className={({ isActive }) =>
                                            `admin-sidebar-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <img
                                            src={AudioIcon}
                                            alt="Ur Studio"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title">UrStudio</span>
                                        )}
                                    </NavLink>
                                </li>

                                {/* Artist Manager */}
                                <li>
                                    <NavLink
                                        to="/admin/artist-manager"
                                        className={({ isActive }) =>
                                            `admin-sidebar-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <img
                                            src={ArtistIcon}
                                            alt="Artist Manager"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title">Artist Manager</span>
                                        )}
                                    </NavLink>
                                </li>

                                {/* Data Analyst */}
                                <li>
                                    <NavLink
                                        to="/admin/data-analyst"
                                        className={({ isActive }) =>
                                            `admin-sidebar-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <img
                                            src={AnalystIcon}
                                            alt="Data Analyst"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title">Data Analyst</span>
                                        )}
                                    </NavLink>
                                </li>

                                {/* Growth Engine */}
                                <li>
                                    <NavLink
                                        to="/admin/growth-engine"
                                        className={({ isActive }) =>
                                            `admin-sidebar-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <img
                                            src={MarketingManagerIcon}
                                            alt="Growth Engine"
                                            className={`${isCollapsed ? "mx-auto" : ""} text-[#00067e]`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title">Growth Engine</span>
                                        )}
                                    </NavLink>
                                </li>
                                <li>
                                    {/* <Link
                                        onClick={() => handleShowAlert("ProfessorU")}
                                        className="admin-sidebar-link opacity-50 flex items-center cursor-not-allowed"
                                    >
                                        <img
                                            src={ProfessorUIcon}
                                            alt="ProfessorU"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title flex items-center gap-1">
                                                ProfessorU <i className="bi bi-wrench" />
                                            </span>
                                        )}
                                    </Link> */}
                                    <NavLink
                                        to="/admin/professor-u"
                                        className={({ isActive }) =>
                                            `admin-sidebar-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <img
                                            src={ProfessorUIcon}
                                            alt="ProfessorU"
                                            className={`${isCollapsed ? "mx-auto" : ""} text-[#00067e]`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title">ProfessorU</span>
                                        )}
                                    </NavLink>
                                </li>

                                {/* Coming Soon - keep as Link */}
                                <li>
                                    <Link
                                        onClick={() => handleShowAlert("Content Creator")}
                                        className="admin-sidebar-link opacity-50 flex items-center cursor-not-allowed"
                                    >
                                        <img
                                            src={CreatorIcon}
                                            alt="Content Creator"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title flex items-center gap-1">
                                                Content Creator <i className="bi bi-wrench" />
                                            </span>
                                        )}
                                    </Link>
                                </li>

                                <li>
                                    <Link
                                        onClick={() => handleShowAlert("Publicist")}
                                        className="admin-sidebar-link opacity-50 flex items-center cursor-not-allowed"
                                    >
                                        <img
                                            src={PublicistIcon}
                                            alt="Publicist"
                                            className={`${isCollapsed ? "mx-auto" : ""}`}
                                        />
                                        {!isCollapsed && (
                                            <span className="menu-title flex items-center gap-1">
                                                Publicist <i className="bi bi-wrench" />
                                            </span>
                                        )}
                                    </Link>
                                </li>

                            </ul>
                        </div>

                        {/* <div className="bottom-section">
                    </div>
                    <div className="middle-section menu-wrapper flex-1 overflow-auto pe-1">
                        <div className="label text-white text-base font-medium mb-1">Roles</div>
                        <ul className="list-none flex flex-col gap-2 2xl:gap-4">
                            <li>
                                <Link to={'/admin/growth-engine'} className="admin-sidebar-link">
                                    <img src={MarketingManagerIcon} alt="Growth Engine" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Growth Engine</span>}
                                </Link>
                            </li>
                            <li>
                                <Link to={'#'} className="admin-sidebar-link">
                                    <img src={PublicistIcon} alt="Publicist" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Publicist</span>}
                                </Link>
                            </li>
                            <li>
                                <Link to={'/admin/artist-manager'} className="admin-sidebar-link">
                                    <img src={ArtistIcon} alt="Artist Manager" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Artist Manager</span>}</Link>
                            </li>
                            <li>
                                <Link to={'/admin/audio-engineer'} className="admin-sidebar-link">
                                    <img src={AudioIcon} alt="Audio Engineer" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Audio Engineer</span>}
                                </Link>
                            </li>
                            <li>
                                <Link to={'#'} className="admin-sidebar-link">
                                    <img src={CreatorIcon} alt="Content Creator" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Content Creator</span>}
                                </Link>
                            </li>
                            <li>
                                <Link to={'/admin/data-analyst'} className="admin-sidebar-link">
                                    <img src={AnalystIcon} alt="Data Analyst" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Data Analyst</span>}
                                </Link>
                            </li>
                            <li>
                                <Link to={'#'} className="admin-sidebar-link">
                                    <img src={ProfessorUIcon} alt="Content Creator" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>ProfessorU</span>}
                                </Link>
                            </li>
                            <li>
                                <Link to={'/admin/amplitude-ai'} className="admin-sidebar-link">
                                    <img src={AmplitudeIcon} alt="Content Creator" className={`${isCollapsed ? 'mx-auto' : ''}`} /> {!isCollapsed && <span className='menu-title'>Amplitude AI</span>}
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div className="bottom-section">
                        <div className="btn-wrapper">
                            <button type='button' className={`bg-theme_clr1 text-sm text-white hover:text-theme_clr2 font-medium flex items-center gap-2 w-full capitalize transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] mb-3 md:mb-4 2xl:mb-7 ${isCollapsed ? 'justify-center rounded-xl' : 'justify-between rounded-full'} px-5 py-2 md:py-3 2xl:py-[15px]`}>
                                {!isCollapsed && <span className='menu-title'>Add Members</span>} <i className="bi bi-plus-circle text-[20px]"></i>
                            </button>
                            <button type='button' className={`sidebar-pro-btn bg-gradient-to-b from-[#F5BA23] from-[0%] to-[#E5A000] to-[93%] border border-white font-jakarta font-extrabold text-sm text-white hover:text-theme_clr2 flex items-center gap-2 w-full uppercase transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] ${isCollapsed ? 'justify-center rounded-xl' : 'justify-between rounded-full'} px-5 py-2 md:py-3 2xl:py-[15px]`}>
                                {!isCollapsed && <span className='menu-title'>{hasActiveSubscription ? 'PRO' : 'Go PRO'}</span>} <img src={StarIcon} alt="Star" className='h-[20px] w-[20px] min-w-[20px]' />
                            </button>
                        </div>
                    </div> */}
                        {/* Bottom Section */}
                        <div className="bottom-section shrink-0 max-h-[46dvh] overflow-y-auto overscroll-contain">
                            <div className="btn-wrapper">
                                {/* <NavLink
                                    to="/admin/teams"
                                    title="Teams"
                                    onClick={closeSidebarOnMobile}
                                    className={({ isActive }) =>
                                        `bg-theme_clr1 border font-jakarta font-extrabold text-sm flex items-center gap-2 w-full uppercase transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] mb-3 md:py-3 cursor-pointer ${isActive ? "border-[#ff6a3d] text-[#ff6a3d]" : "border-white text-white"} ${isCollapsed ? "justify-center rounded-xl px-2 py-2" : "justify-between rounded-full px-5 py-2"}`
                                    }
                                >
                                    {!isCollapsed && <span className="menu-title">Teams</span>}
                                    <i className="bi bi-people text-[18px] leading-none" />
                                </NavLink> */}
                                {/* Distribution button*/}
                                <button
                                    type="button"
                                    data-tour="distribution-button"
                                    title="Distribution"
                                    onClick={() => {
                                        handleShowDistribution();
                                        closeSidebarOnMobile();
                                    }}
                                    className={`sidebar-pro-btn bg-theme_clr4 border border-white font-jakarta font-extrabold text-sm text-white flex items-center gap-2 w-full uppercase transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] mb-3 md:py-3 cursor-pointer ${isCollapsed ? 'justify-center rounded-xl px-2 py-2' : 'justify-between rounded-full px-5 py-2'}`}
                                >
                                    {!isCollapsed && <span className="menu-title">Distribution</span>}
                                    <img
                                        src={DistributionIcon}
                                        alt="distribution"
                                        style={{ filter: "brightness(0) invert(1)" }}
                                        className="h-[20px] w-[20px] min-w-[20px]"
                                    />
                                </button>
                                {/* Credits Progress */}
                                {!isBillingResolved ? (
                                    <div className="space-y-3 mb-3">
                                        <div className={`animate-pulse bg-white/20 rounded-lg ${isCollapsed ? "h-10" : "h-16"} w-full`} />
                                        <div className={`animate-pulse bg-white/25 rounded-full h-11 w-full`} />
                                    </div>
                                ) : credits && credits.total > 0 ? (
                                    <div
                                        className={`bg-gradient-to-r from-theme_clr1 to-theme_clr2 text-sm text-white p-3 rounded-lg mb-3 ${isCollapsed ? "text-center" : ""
                                            }`}
                                        data-tour="ai-credits-progress"
                                    >
                                        {!isCollapsed && (
                                            <>
                                                <div className="flex items-center justify-between gap-1 mb-1">
                                                    <span className="text-xs font-semibold">
                                                        AI Credits {credits?.remaining}/{credits?.total}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        title="Get More Credits"
                                                        onClick={() => {
                                            handleOpenCreditModal();
                                            closeSidebarOnMobile();
                                        }}
                                                        className="p-1.5 rounded-full hover:bg-white/20 active:scale-95 transition-all duration-200 focus:outline-none"
                                                    >
                                                        <i className="bi bi-plus-circle text-lg hover:text-yellow-400 transition-all duration-200"></i>
                                                    </button>
                                                </div>

                                                {/* Progress bar */}
                                                <div className="w-full bg-gray-500 rounded-full h-2">
                                                    <div
                                                        className="bg-yellow-500 h-2 rounded-full"
                                                        style={{ width: `${progress}%` }}
                                                    ></div>
                                                </div>
                                            </>
                                        )}

                                        {isCollapsed && (
                                            <button
                                                type="button"
                                                title="Get More Credits"
                                                onClick={() => handleOpenCreditModal()}
                                                className="p-1 rounded-full hover:bg-white/20 active:scale-95 transition-all duration-200"
                                            >
                                                <i className="bi bi-plus-circle text-[20px] hover:text-yellow-400 transition-all"></i>
                                            </button>
                                        )}
                                    </div>

                                ) : (
                                    <button type='button' className={`bg-theme_clr1 text-sm text-white hover:text-theme_clr2 font-medium flex items-center gap-2 w-full capitalize transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] mb-3 md:mb-4 2xl:mb-7 ${isCollapsed ? 'justify-center rounded-xl' : 'justify-between rounded-full'} px-5 py-2 md:py-3 2xl:py-[15px]`}
                                        onClick={() => {
                                            handleOpenCreditModal();
                                            closeSidebarOnMobile();
                                        }}
                                    >
                                        {!isCollapsed && <span className='menu-title'>Get Credits</span>} <i className="bi bi-plus-circle text-[20px]"></i>
                                    </button>
                                )}

                                {showLinkArtistButton && (
                                    <button
                                        type="button"
                                        data-tour="link-artist-button"
                                        title="Link Artist"
                                        onClick={handleLinkArtistClick}
                                        className={`sidebar-pro-btn bg-theme_clr4 border border-white font-jakarta font-semibold text-xs text-white flex items-center gap-2 w-full transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] mb-2 hover:bg-[#e55532] hover:text-white active:scale-[0.98] ${isCollapsed ? 'justify-center rounded-lg' : 'justify-between rounded-full'
                                            } px-3 py-1.5 md:py-2 cursor-pointer`}
                                    >
                                        {!isCollapsed && (
                                            <span className="menu-title">Link Artist</span>
                                        )}
                                        <img
                                            src={ArtistIcon}
                                            alt=""
                                            className="h-[16px] w-[16px] min-w-[16px]"
                                            style={{ filter: "brightness(0) invert(1)" }}
                                        />
                                    </button>
                                )}

                                {!isBillingResolved ? (
                                    <div className="animate-pulse bg-white/25 rounded-full h-11 w-full" />
                                ) : (
                                    <button
                                        type="button"
                                        data-tour="pro-button"
                                        title={hasActiveSubscription ? "PRO" : "Go PRO"}
                                        disabled={loading}
                                        onClick={() => {
                                            handleGoProClick();
                                            closeSidebarOnMobile();
                                        }}
                                        className={`sidebar-pro-btn bg-gradient-to-b from-[#F5BA23] to-[#E5A000] border border-white font-jakarta font-extrabold text-sm text-white hover:text-theme_clr2 flex items-center gap-2 w-full uppercase transition-all drop-shadow-[0_21px_27px_rgba(96,60,255,0.48)] ${isCollapsed ? 'justify-center rounded-xl' : 'justify-between rounded-full'
                                            } px-5 py-2 md:py-3 ${loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                                    >
                                        {!isCollapsed && (
                                            <span className={`menu-title ${loading ? "opacity-80" : ""}`}>
                                                {hasActiveSubscription ? "PRO" : "Go PRO"}
                                            </span>
                                        )}

                                        {loading ? (
                                            <div className="h-[20px] w-[20px] min-w-[20px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <img
                                                src={StarIcon}
                                                alt=""
                                                className="h-[20px] w-[20px] min-w-[20px]"
                                            />
                                        )}
                                    </button>
                                )}

                            </div>
                        </div>
                    </div>
                </nav>
            </aside>
            {/* Distribution Modal */}
            <DistributionModal
                isOpen={showDistributionModal}
                onClose={handleShowDistribution}
            />
            {/* Pricing Plan Modal */}
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={actions.getBillingReturnUrl()}
            />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />

            {showLinkArtistRequiredModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 relative animate-fadeIn">
                        <button
                            type="button"
                            className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                            onClick={() => setShowLinkArtistRequiredModal(false)}
                            aria-label="Close"
                        >
                            <i className="bi bi-x-lg" />
                        </button>
                        <h2 className="text-xl font-semibold text-theme_clr1 mb-3 pr-8">
                            Link your artist first
                        </h2>
                        <p className="text-sm text-slate-600 mb-6">
                            Before upgrading to UpNComer Pro, link your artist profile so we can unlock
                            analytics across streams, fanbase, listeners, and more.
                        </p>
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowLinkArtistRequiredModal(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                            >
                                Not now
                            </button>
                            <button
                                type="button"
                                onClick={handleLinkArtistClick}
                                className="btn-custom3 !py-2 !px-5"
                            >
                                Link Your Artist
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
